<template>
    <div>
        <DataTable
            :perPage="itemsPerPage"
            :columns="tableColumns"
            :items="paginatedItems"
            :currentPage="currentPage"
            :totalPages="totalPages"
            :selectedItems="selectedItems"
            :enableDragDrop="true"
            :enableMultiSelect="true"
            @page-changed="handlePageChange"
            @item-selected="toggleItemSelection"
            @select-all-toggled="toggleSelectAll"
            @drag-end="handleDragEnd"
            @per-page-changed="handlePerPageChange"
            @sort="handleSort"
        >
            <!-- Toolbar -->
            <template #toolbar>
                <div class="ppv2-flex md:ppv2-flex-row ppv2-flex-col ppv2-gap-2 ppv2-justify-between ppv2-items-center ppv2-mb-6">
                    <div class="ppv2-flex ppv2-flex-wrap ppv2-gap-3">
                        <!-- Search Input -->
                        <div class="ppv2-relative">
                            <Search v-model="searchQuery" :emitGlobal="false" @search-updated="handleSearchUpdate" />
                        </div>

                        <!-- Export Button -->
                        <ExportSelect
                            :options="[
                                { label: 'CSV', value: 'csv' },
                                { label: 'Excel', value: 'excel' },
                            ]"
                            :disabled="isExporting"
                            @export-selected="exportData"
                        />

                        <!-- Filter Button -->
                        <button
                            class="filterButton ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-rounded-xl ppv2-border ppv2-border-solid ppv2-border-black ppv2-border-opacity-20 ppv2-text-black hover:ppv2-bg-opacity-80 ppv2-transition-all"
                        >
                            <LayoutGrid :size="17" />
                            <span class="poppinsLight" :style="{ fontSize: normalizeFontSize(15) }">
                                {{ $t('products.actions.filter') }}
                            </span>
                            <ChevronDown :size="16" class="ppv2-text-gray-400" />
                        </button>
                    </div>

                    <div class="ppv2-relative" ref="addDropdown">
                        <BaseSplitButton
                            :label="$t('common.add')"
                            :icon="Plus"
                            :showDropdown="showAddDropdown"
                            @click="showAddDropdown = !showAddDropdown"
                            @toggle="showAddDropdown = !showAddDropdown"
                        />

                        <div
                            v-if="showAddDropdown"
                            class="menuDropdown ppv2-absolute ppv2-right-0 ppv2-top-full ppv2-overflow-hidden ppv2-shadow-lg ppv2-z-50"
                        >
                            <button
                                @click="openAddOptionGroupModal"
                                class="dropdownItem ppv2-w-full ppv2-text-left ppv2-px-4 ppv2-py-2 ppv2-transition-colors ppv2-flex ppv2-items-center ppv2-gap-3"
                            >
                                <Plus :size="16" />
                                <span class="poppinsLight ppv2-text-gray-700" :style="{ fontSize: normalizeFontSize(14) }">{{
                                    $t('food.options.add_option')
                                }}</span>
                            </button>
                            <button
                                @click="openAddSupplementGroupModal"
                                class="dropdownItem ppv2-w-full ppv2-text-left ppv2-px-4 ppv2-py-2 ppv2-transition-colors ppv2-flex ppv2-items-center ppv2-gap-3"
                            >
                                <Plus :size="16" />
                                <span class="poppinsLight ppv2-text-gray-700" :style="{ fontSize: normalizeFontSize(14) }">{{
                                    $t('food.options.add_supplement')
                                }}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </template>

            <!-- Column: Name -->
            <template #column-name="{ item }">
                <span class="ppv2-text-black ppv2-underline" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ item.name }}
                </span>
            </template>

            <!-- Column: Price -->
            <template #column-price="{ item }">
                <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                    <template
                        v-if="
                            item.type === 'attribute' ||
                            (item.category_name && item.category_name.toLowerCase().includes('option'))
                        "
                    >
                        -
                    </template>
                    <template v-else> {{ formatPrice(item.price) }} {{ $symbolCurrency }} </template>
                </span>
            </template>

            <!-- Column: Category -->
            <template #column-category="{ item }">
                <span> {{ item.category_name || $t('food.options.unclassified') }} </span>
            </template>

            <!-- Column: Status -->
            <template #column-status="{ item }">
                <button
                    @click="toggleStatus(item)"
                    class="ppv2-min-w-[125px] ppv2-inline-flex ppv2-items-center ppv2-gap-2 ppv2-px-3 ppv2-py-2 ppv2-rounded-xl ppv2-border ppv2-border-solid ppv2-transition-all"
                    :class="
                        item.status === 'actif'
                            ? 'ppv2-border-[#206A64] ppv2-bg-[#06B217] ppv2-bg-opacity-20'
                            : 'ppv2-border-[#4E4E4E] ppv2-bg-[#474747] ppv2-bg-opacity-20'
                    "
                >
                    <div
                        class="ppv2-w-9 ppv2-h-5 ppv2-rounded-full ppv2-flex ppv2-items-center ppv2-transition-all"
                        :class="
                            item.status === 'actif'
                                ? 'ppv2-bg-[#206A64] ppv2-justify-end'
                                : 'ppv2-bg-[#4E4E4E] ppv2-justify-start'
                        "
                    >
                        <div class="ppv2-w-3.5 ppv2-h-3.5 ppv2-bg-white ppv2-rounded-full ppv2-mx-0.5"></div>
                    </div>
                    <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                        {{ item.status === 'actif' ? $t('food.status.active') : $t('food.status.inactive') }}
                    </span>
                </button>
            </template>

            <!-- Column: Stock -->
            <template #column-stock="{ item }">
                <span v-if="!item.manage_stock" class="poppinsLight ppv2-text-black" :style="{ fontSize: normalizeFontSize(14) }">
                    &infin;
                </span>
                <span
                    v-else
                    class="poppinsRegular ppv2-flex ppv2-items-center ppv2-gap-1"
                    :class="item.stock < 5 ? 'ppv2-text-[#911414]' : 'ppv2-text-black'"
                    :style="{ fontSize: normalizeFontSize(14) }"
                >
                    {{ item.stock }}
                    <TriangleAlert v-if="item.stock < 5" :size="18" color="#911414" />
                </span>
            </template>

            <!-- Column: Actions -->
            <template #column-actions="{ item }">
                <div class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-3">
                    <button
                        @click="openEditTab(item)"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :title="$t('common.edit')"
                    >
                        <SquarePen :size="18" />
                    </button>
                    <button
                        @click="duplicateItem(item)"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :title="$t('products.actions.duplicate')"
                    >
                        <CopyPlus :size="18" />
                    </button>
                    <button
                        class="ppv2-p-1 ppv2-text-[#FF9C9C] hover:ppv2-bg-[#FF9C9C] hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :title="$t('common.delete')"
                        @click="deleteSingleItem(item)"
                    >
                        <Trash2 :size="18" />
                    </button>
                </div>
            </template>

            <!-- Bulk Actions Slot -->
            <template #bulk-actions="{ selectedCount }">
                <span class="poppinsRegular ppv2-text-black" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ $t('food.messages.selected_items', selectedCount, { count: selectedCount }) }}
                </span>
                <BaseButton
                    variant="dangerOutline"
                    @click="deleteSelectedItems"
                    customClass="!ppv2-h-[30px] !ppv2-flex !ppv2-items-center !ppv2-gap-2"
                >
                    <Trash2 :size="18" />
                    <span :style="{ fontSize: normalizeFontSize(14) }">{{ $t('common.delete') }}</span>
                </BaseButton>
            </template>
        </DataTable>

        <!-- Delete Confirmation Popup -->
        <CustomDialog v-model="showDeletePopup" :defaultTitle="$t('common.delete')" customClassTitle="poppinsSemiBold" noScroll>
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ deletePopupDescription }}
                </p>
            </template>
            <template #actions>
                <BaseButton
                    type="button"
                    variant="outline"
                    @click="showDeletePopup = false"
                    :customClass="`!ppv2-h-[45px] !ppv2-text-base !ppv2-min-w-[170px]`"
                >
                    {{ $t('common.cancel') }}
                </BaseButton>
                <BaseButton
                    type="submit"
                    variant="danger"
                    @click="confirmDelete"
                    :customClass="`!ppv2-h-[45px] !ppv2-text-base !ppv2-min-w-[170px]`"
                >
                    {{ $t('common.delete') }}
                </BaseButton>
            </template>
        </CustomDialog>

        <!-- Add Option/Supplement Modal -->
        <CustomDialog v-model="showAddModal" :defaultTitle="addModalTitle" width="70%">
            <template #content>
                <!-- Info Message -->
                <div
                    class="ppv2-flex ppv2-items-start ppv2-gap-3 ppv2-px-2 ppv2-py-3 ppv2-bg-[#2C3FE8] ppv2-bg-opacity-10 ppv2-rounded-[10px] ppv2-mb-6 ppv2-border ppv2-border-[#2C3FE8]"
                >
                    <Info :size="20" class="ppv2-text-[#2C3FE8] ppv2-flex-shrink-0 ppv2-mt-0.5" />
                    <div class="ppv2-text-sm ppv2-text-black poppinsRegular">
                        <p class="ppv2-mb-0">
                            {{ $t('food.options.info_option') }}
                        </p>
                    </div>
                </div>

                <!-- Category Type Selector -->
                <BaseSelect
                    v-model="optionForm.categoryId"
                    :label="$t('food.options.type')"
                    :required="true"
                    :options="optionCategories"
                    optionLabel="name"
                    optionValue="id"
                    customLabelClass="poppinsLight"
                    :placeholder="$t('food.options.select_type')"
                    customInputClass="!ppv2-mb-[20px]"
                    fixed-dropdown
                />

                <!-- Name -->
                <CustomInput
                    v-model="optionForm.name"
                    :label="$t('food.options.name_label')"
                    :placeholder="$t('food.options.name_placeholder')"
                />

                <!-- Price -->
                <div class="ppv2-grid ppv2-grid-cols-2 ppv2-mb-4" :style="{ gap: normalizeSize(16, true) }">
                    <CustomInput
                        v-model="optionForm.price"
                        :label="$t('food.options.price_label')"
                        type="number"
                        step="any"
                        :min="0"
                        placeholder="0,00"
                    />
                    <BaseSelect
                        v-model="optionForm.vatRate"
                        :label="$t('food.vat_rate')"
                        :options="vatRates"
                        optionLabel="label"
                        optionValue="value"
                        placeholder="- Sélectionner -"
                        customLabelClass="poppinsLight"
                        customInputClass="!ppv2-mb-[20px]"
                        fixed-dropdown
                    />
                </div>

                <!-- Description -->
                <CustomInput
                    v-model="optionForm.description"
                    :label="$t('food.options.description_label')"
                    :placeholder="$t('food.options.description_placeholder')"
                />

                <!-- Active Toggle -->
                <div class="ppv2-flex ppv2-items-center ppv2-gap-3">
                    <label class="ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-cursor-pointer">
                        <div
                            @click="optionForm.active = !optionForm.active"
                            class="ppv2-w-12 ppv2-h-6 ppv2-rounded-full ppv2-flex ppv2-items-center ppv2-transition-all ppv2-cursor-pointer"
                            :class="
                                optionForm.active ? 'ppv2-bg-[#7D2DBD] ppv2-justify-end' : 'ppv2-bg-gray-300 ppv2-justify-start'
                            "
                        >
                            <div class="ppv2-w-5 ppv2-h-5 ppv2-bg-white ppv2-rounded-full ppv2-mx-0.5 ppv2-shadow"></div>
                        </div>
                        <span class="poppinsRegular ppv2-text-sm">{{ $t('food.status.active') }}</span>
                    </label>
                </div>
            </template>
            <template #actions>
                <BaseButton variant="primary" @click="saveOption" :disabled="!optionForm.name || !optionForm.categoryId">{{
                    $t('common.confirm')
                }}</BaseButton>
            </template>
        </CustomDialog>

        <!-- New: Create Option Group Modal -->
        <CustomDialog
            v-model="showOptionGroupModal"
            :defaultTitle="isEditMode ? $t('food.options.modal_title_option') : $t('food.options.add_option')"
            heightClass="ppv2-max-h-[85vh]"
        >
            <template #content>
                <!-- Info Message -->
                <div
                    class="ppv2-flex ppv2-items-start ppv2-gap-3 ppv2-px-2 ppv2-py-3 ppv2-bg-[#2C3FE8] ppv2-bg-opacity-10 ppv2-rounded-[10px] ppv2-mb-6 ppv2-border ppv2-border-[#2C3FE8]"
                >
                    <Info :size="20" class="ppv2-text-[#2C3FE8] ppv2-flex-shrink-0 ppv2-mt-0.5" />
                    <div class="ppv2-text-sm ppv2-text-black poppinsRegular">
                        <p class="ppv2-mb-0">
                            {{ $t('food.options.info_option_group') }}
                        </p>
                    </div>
                </div>

                <!-- Group Name -->
                <CustomInput
                    v-model="optionGroupForm.name"
                    :label="$t('food.options.group_name_label')"
                    :placeholder="$t('food.options.group_name_placeholder')"
                    :required="true"
                    :state="getState('name')"
                    :errorText="errors.name"
                    @input="clearError('name')"
                    :customSizeLabel="13"
                />

                <!-- Items List -->
                <div class="ppv2-flex ppv2-flex-col" :style="{ gap: normalizeSize(8, true) }">
                    <div
                        v-for="(item, index) in optionGroupForm.items"
                        :key="'opt-item-' + index"
                        class="opt-drag-item ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-border ppv2-border-solid ppv2-border-[#dcdcdc] ppv2-bg-opacity-20 ppv2-px-3 ppv2-py-2 ppv2-rounded-[12px]"
                        draggable="true"
                        @dragstart="onOptDragStart($event, index)"
                        @dragover.prevent="onOptDragOver($event)"
                        @drop="onOptDrop($event, index)"
                        @dragenter="onOptDragEnter($event)"
                        @dragleave="onOptDragLeave($event)"
                        @dragend="onOptDragEnd($event)"
                    >
                        <GripVertical :size="16" color="#9D96A8" class="ppv2-cursor-grab ppv2-flex-shrink-0" />

                        <div class="ppv2-flex-1">
                            <CustomInput
                                v-model="item.name"
                                :placeholder="$t('food.options.option_item_placeholder') + ' ' + (index + 1)"
                                :state="getItemState('name', index)"
                                :errorText="errors.items?.[index]?.name"
                                classContainerAdd="!ppv2-mb-0"
                                @input="clearItemError('name', index)"
                                :customSizeLabel="11"
                            />
                        </div>

                        <div class="ppv2-w-28">
                            <CustomInput
                                :label="$t('food.columns.price')"
                                :floatingLabel="true"
                                :right-icon="$symbolCurrency"
                                v-model="item.price"
                                type="number"
                                :min="0"
                                placeholder="0,00"
                                step="any"
                                :state="getItemState('price', index)"
                                :errorText="errors.items?.[index]?.price"
                                classContainerAdd="!ppv2-mb-0"
                                @input="clearItemError('price', index)"
                                :customSizeLabel="11"
                            />
                        </div>

                        <div class="ppv2-w-24">
                            <BaseSelect
                                :label="$t('food.vat_rate')"
                                :floatingLabel="true"
                                v-model="item.vat_rate"
                                :options="vatRates"
                                placeholder="-"
                                customLabelClass="poppinsLight"
                                customInputClass="!ppv2-mb-0 ppv2-min-w-[90px]"
                                fixed-dropdown
                                :customSizeLabel="11"
                            />
                        </div>

                        <button
                            type="button"
                            @click="removeOptionGroupItem(index)"
                            :disabled="optionGroupForm.items.length <= 1"
                            class="ppv2-relative ppv2-h-[38px] ppv2-w-[34px] ppv2-top-[4px] ppv2-flex-shrink-0 ppv2-p-1 ppv2-rounded ppv2-text-[#6b7480] ppv2-border ppv2-border-solid ppv2-border-[#dcdcdc] hover:ppv2-text-[#b04141] hover:ppv2-bg-[#e0b4b4] hover:ppv2-border-[#b04141] ppv2-transition-all disabled:ppv2-opacity-30"
                        >
                            <X :size="16" />
                        </button>
                    </div>
                </div>

                <button
                    type="button"
                    @click="addOptionGroupItem"
                    class="ppv2-w-full ppv2-mt-3 ppv2-py-2 ppv2-rounded-[10px] ppv2-border ppv2-border-dashed ppv2-border-[#7D2DBD] ppv2-text-[#7D2DBD] ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-2 hover:ppv2-bg-[#7D2DBD] hover:ppv2-bg-opacity-5 ppv2-transition-all poppinsRegular"
                    style="font-size: 14px"
                >
                    <Plus :size="15" />
                    {{ $t('food.options.add_option') }}
                </button>
            </template>
            <template #actions>
                <BaseButton
                    variant="primary"
                    @click="saveOptionGroup"
                    :disabled="!optionGroupForm.name"
                    :customClass="`!ppv2-h-[45px] !ppv2-min-w-[170px]`"
                    >{{ $t('common.confirm') }}</BaseButton
                >
            </template>
        </CustomDialog>

        <!-- Duplicate Confirmation Popup -->
        <CustomDialog v-model="showDuplicatePopup" :defaultTitle="$t('common.duplicate')" height="250px" minHeight="250px">
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular">{{ duplicatePopupDescription }}</p>
            </template>
            <template #actions>
                <BaseButton
                    variant="outline"
                    @click="showDuplicatePopup = false"
                    :customClass="`!ppv2-h-[45px] !ppv2-min-w-[170px]`"
                    >{{ $t('common.cancel') }}</BaseButton
                >
                <BaseButton variant="primary" @click="confirmDuplicate" :customClass="`!ppv2-h-[45px] !ppv2-min-w-[170px]`">{{
                    $t('common.confirm')
                }}</BaseButton>
            </template>
        </CustomDialog>

        <!-- New: Create Supplement Group Modal -->
        <CustomDialog
            v-model="showSupplementGroupModal"
            :defaultTitle="isEditMode ? $t('food.options.modal_title_supplement') : $t('food.options.add_supplement')"
            heightClass="ppv2-max-h-[85vh]"
        >
            <template #content>
                <!-- Info Message -->
                <div
                    class="ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-2 ppv2-py-3 ppv2-bg-[#2C3FE8] ppv2-bg-opacity-10 ppv2-rounded-[10px] ppv2-mb-6 ppv2-border ppv2-border-[#2C3FE8]"
                >
                    <Info :size="20" class="ppv2-text-[#2C3FE8] ppv2-flex-shrink-0 ppv2-mt-0.5" />
                    <div class="ppv2-text-sm ppv2-text-black poppinsRegular">
                        <p class="ppv2-mb-0">
                            {{ $t('food.options.info_supplement_group') }}
                        </p>
                    </div>
                </div>

                <!-- Group Name -->
                <CustomInput
                    v-model="supplementGroupForm.name"
                    :label="$t('food.options.supplement_name_label')"
                    :required="true"
                    :placeholder="$t('food.options.supplement_name_placeholder')"
                    :error="errors.name"
                    @input="clearError('name')"
                    @focus="activeField = 'name'"
                    @blur="activeField = null"
                />

                <!-- Constraints -->
                <div class="ppv2-flex ppv2-items-end" :style="{ gap: normalizeSize(16, true) }">
                    <div class="ppv2-flex-1">
                        <CustomInput
                            v-model="supplementGroupForm.min_quantity"
                            :label="$t('food.options.min_quantity')"
                            type="number"
                            placeholder="0"
                            :min="1"
                            :error="errors.min_quantity"
                            @input="clearError('min_quantity')"
                            @focus="activeField = 'min_quantity'"
                            @blur="activeField = null"
                        />
                    </div>
                    <div class="ppv2-flex-1">
                        <CustomInput
                            v-model="supplementGroupForm.max_quantity"
                            :label="$t('food.options.max_quantity')"
                            type="number"
                            placeholder="0"
                            :min="1"
                            :error="errors.max_quantity"
                            @input="clearError('max_quantity')"
                            @focus="activeField = 'max_quantity'"
                            @blur="activeField = null"
                        />
                    </div>
                    <div class="ppv2-flex ppv2-items-center ppv2-mb-[20px] ppv2-h-[40px]">
                        <BaseSwitch v-model="supplementGroupForm.optional" :label="$t('food.options.optional')" size="sm" />
                    </div>
                </div>

                <!-- Items List -->
                <div class="ppv2-flex ppv2-flex-col" :style="{ gap: normalizeSize(8, true) }">
                    <div
                        class="supp-drag-item ppv2-flex ppv2-flex-col ppv2-bg-[#dcdcdc] ppv2-bg-opacity-20 ppv2-px-3 ppv2-py-2 ppv2-rounded-[12px]"
                        v-for="(item, index) in supplementGroupForm.items"
                        :key="'supp-item-' + index"
                        draggable="true"
                        @dragstart="onSuppDragStart($event, index)"
                        @dragover.prevent="onSuppDragOver($event)"
                        @drop="onSuppDrop($event, index)"
                        @dragenter="onSuppDragEnter($event)"
                        @dragleave="onSuppDragLeave($event)"
                        @dragend="onSuppDragEnd($event)"
                    >
                        <!-- Row 1: Handle, Checkbox, Name, Price, VAT, X -->
                        <div class="ppv2-w-full ppv2-flex ppv2-items-center ppv2-gap-2">
                            <GripVertical :size="16" color="#9D96A8" class="ppv2-cursor-grab ppv2-flex-shrink-0" />

                            <div class="ppv2-flex ppv2-items-center ppv2-flex-shrink-0">
                                <BaseCheckbox
                                    :value="item.active !== false"
                                    @input="item.active = $event"
                                    size="md"
                                    :hasBorderChecked="true"
                                />
                            </div>

                            <div class="ppv2-flex-1">
                                <CustomInput
                                    v-model="item.name"
                                    :placeholder="$t('food.options.supplement_item_placeholder') + ' ' + (index + 1)"
                                    :state="getItemState('name', index)"
                                    :errorText="errors.items?.[index]?.name"
                                    classContainerAdd="!ppv2-mb-0"
                                    @input="clearItemError('name', index)"
                                />
                            </div>

                            <div class="ppv2-w-28">
                                <CustomInput
                                    :label="$t('food.columns.price')"
                                    :floatingLabel="true"
                                    v-model="item.price"
                                    type="number"
                                    placeholder="0"
                                    :right-icon="$symbolCurrency"
                                    step="any"
                                    :state="item.is_free ? 'disabled' : getItemState('price', index)"
                                    classContainerAdd="!ppv2-mb-0"
                                    @input="clearItemError('price', index)"
                                />
                            </div>

                            <div class="ppv2-w-24">
                                <BaseSelect
                                    :label="$t('food.vat_rate')"
                                    :floatingLabel="true"
                                    v-model="item.vat_rate"
                                    :options="vatRates"
                                    placeholder="-"
                                    customLabelClass="poppinsLight"
                                    customInputClass="!ppv2-mb-0 ppv2-min-w-[90px]"
                                    fixed-dropdown
                                />
                            </div>

                            <button
                                type="button"
                                @click="removeSupplementGroupItem(index)"
                                :disabled="supplementGroupForm.items.length <= 1"
                                class="ppv2-flex-shrink-0 ppv2-p-1 ppv2-rounded ppv2-text-[#FF9C9C] hover:ppv2-bg-[#FF9C9C] hover:ppv2-bg-opacity-10 ppv2-transition-all disabled:ppv2-opacity-30"
                            >
                                <X :size="16" />
                            </button>
                        </div>

                        <!-- Row 2: Validation Error -->
                        <div
                            v-if="errors.items?.[index]?.price"
                            class="ppv2-flex ppv2-ml-12 ppv2-mt-1 ppv2-text-[#E02D3C] ppv2-text-xs poppinsRegular"
                        >
                            {{ errors.items[index].price }}
                        </div>

                        <!-- Row 3: Toggles -->
                        <div class="ppv2-flex ppv2-items-center ppv2-gap-6 ppv2-ml-12 ppv2-mt-1">
                            <!-- Inclus (is_free) -->
                            <label class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-cursor-pointer">
                                <div
                                    @click="toggleFree(item)"
                                    class="ppv2-w-10 ppv2-h-5 ppv2-rounded-full ppv2-flex ppv2-items-center ppv2-transition-all ppv2-cursor-pointer"
                                    :class="
                                        item.is_free
                                            ? 'ppv2-bg-[#7D2DBD] ppv2-justify-end'
                                            : 'ppv2-bg-gray-300 ppv2-justify-start'
                                    "
                                >
                                    <div class="ppv2-w-4 ppv2-h-4 ppv2-bg-white ppv2-rounded-full ppv2-mx-0.5 ppv2-shadow"></div>
                                </div>
                                <span class="poppinsRegular ppv2-text-sm ppv2-text-gray-700">{{
                                    $t('food.options.included')
                                }}</span>
                            </label>

                            <!-- Obligatoire (is_required) -->
                            <label class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-cursor-pointer">
                                <div
                                    @click="item.is_required = !item.is_required"
                                    class="ppv2-w-10 ppv2-h-5 ppv2-rounded-full ppv2-flex ppv2-items-center ppv2-transition-all ppv2-cursor-pointer"
                                    :class="
                                        item.is_required
                                            ? 'ppv2-bg-[#7D2DBD] ppv2-justify-end'
                                            : 'ppv2-bg-gray-300 ppv2-justify-start'
                                    "
                                >
                                    <div class="ppv2-w-4 ppv2-h-4 ppv2-bg-white ppv2-rounded-full ppv2-mx-0.5 ppv2-shadow"></div>
                                </div>
                                <span class="poppinsRegular ppv2-text-sm ppv2-text-gray-700">{{
                                    $t('food.options.required')
                                }}</span>
                            </label>

                            <!-- Limiter quantité à 1 (force_one_qty) -->
                            <label class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-cursor-pointer">
                                <div
                                    @click="item.force_one_qty = !item.force_one_qty"
                                    class="ppv2-w-10 ppv2-h-5 ppv2-rounded-full ppv2-flex ppv2-items-center ppv2-transition-all ppv2-cursor-pointer"
                                    :class="
                                        item.force_one_qty
                                            ? 'ppv2-bg-[#7D2DBD] ppv2-justify-end'
                                            : 'ppv2-bg-gray-300 ppv2-justify-start'
                                    "
                                >
                                    <div class="ppv2-w-4 ppv2-h-4 ppv2-bg-white ppv2-rounded-full ppv2-mx-0.5 ppv2-shadow"></div>
                                </div>
                                <span class="poppinsRegular ppv2-text-sm ppv2-text-gray-700">{{
                                    $t('food.options.limit_one')
                                }}</span>
                            </label>
                        </div>
                    </div>
                </div>

                <button
                    type="button"
                    @click="addSupplementGroupItem"
                    class="ppv2-w-full ppv2-mt-3 ppv2-py-2 ppv2-rounded-[10px] ppv2-border ppv2-border-dashed ppv2-border-[#7D2DBD] ppv2-text-[#7D2DBD] ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-2 hover:ppv2-bg-[#7D2DBD] hover:ppv2-bg-opacity-5 ppv2-transition-all poppinsRegular"
                    style="font-size: 14px"
                >
                    <Plus :size="15" />
                    {{ $t('food.options.add_supplement') }}
                </button>
            </template>
            <template #actions>
                <BaseButton
                    variant="primary"
                    @click="saveSupplementGroup"
                    :disabled="!supplementGroupForm.name"
                    :customClass="`!ppv2-h-[45px] !ppv2-min-w-[170px]`"
                    >{{ $t('common.confirm') }}</BaseButton
                >
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import {
    LayoutGrid,
    TriangleAlert,
    ChevronDown,
    Plus,
    Minus,
    SquarePen,
    CopyPlus,
    Trash2,
    Info,
    GripVertical,
    X,
} from 'lucide-vue'
import normalizeSize from '../../../utils/normalizeSize'
import Search from '../Experience/components/Search.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import BaseSplitButton from '../../Shared/BaseSplitButton.vue'
import CustomInput from '../../Inputs/CustomInput.vue'
import BaseSelect from '../../Shared/BaseSelect.vue'
import BaseCheckbox from '../../Shared/BaseCheckbox.vue'
import BaseSwitch from '../../Shared/BaseSwitch.vue'
import CustomDialog from '../../Shared/CustomDialog.vue'
import DataTable from '../../Shared/DataTable.vue'
import ExportSelect from '../../Shared/ExportSelect.vue'
import axios from 'axios'
import normalizeFontSize from '../../../utils/normalizeFontSize'
import { getPerPage, setPerPage } from '../../../utils/perPage'

export default {
    name: 'BoFoodV3Options',
    components: {
        LayoutGrid,
        ChevronDown,
        Plus,
        Minus,
        SquarePen,
        CopyPlus,
        Trash2,
        Info,
        GripVertical,
        X,
        Search,
        BaseButton,
        BaseSplitButton,
        CustomInput,
        BaseSelect,
        BaseCheckbox,
        BaseSwitch,
        CustomDialog,
        DataTable,
        TriangleAlert,
        ExportSelect,
    },
    props: {
        establishment: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            Plus,
            categorySlugs: ['complements', 'supplements', 'boissons'],
            activeField: null,
            errors: {
                items: [],
            },
            searchQuery: '',
            currentPage: 1,
            itemsPerPage: getPerPage('food_options_items_per_page', 10),
            selectedItems: [],
            sortBy: 'order',
            sortOrder: 'asc',
            isSavingPositions: false,
            showDeletePopup: false,
            deletePopupDescription: '',
            deleteAction: null,
            itemToDelete: null,

            // Duplicate Popup
            showDuplicatePopup: false,
            duplicatePopupDescription: '',
            itemToDuplicate: null,

            items: [],
            totalPages: 1,
            isExporting: false,
            // Add Option Modal
            showAddModal: false,
            optionCategories: [],
            // Dropdown State
            showAddDropdown: false,

            // New Modals State
            showOptionGroupModal: false,
            showSupplementGroupModal: false,

            // Option Group Form
            optionGroupForm: {
                name: '',
                items: [{ name: '', price: 0, vat_rate: null }],
            },

            // Supplement Group Form
            supplementGroupForm: {
                name: '',
                min_quantity: 0,
                max_quantity: 0,
                optional: true,
                items: [
                    {
                        name: '',
                        price: 0,
                        original_price: 0,
                        vat_rate: null,
                        is_free: false,
                        is_required: false,
                        force_one_qty: false,
                        active: true,
                    },
                ],
            },

            // Edit Mode State
            isEditMode: false,
            editingId: null,
            editingSource: 'food_attributes', // 'food_attributes' or 'food_category_addons'

            // Option Form for adding individual options
            optionForm: {
                categoryId: null,
                name: '',
                price: 0,
                vatRate: null,
                description: '',
                active: true,
            },
            activeTab: 'options', // Helper to track active view if needed
        }
    },
    computed: {
        tableColumns() {
            return [
                { key: 'name', label: this.$t('food.columns.name'), sortable: true, type: 'text' },
                { key: 'price', label: this.$t('food.columns.price'), sortable: true, type: 'number' },
                { key: 'category', label: this.$t('food.columns.type'), sortable: true, type: 'text' },
                { key: 'status', label: this.$t('food.columns.status'), sortable: true, type: 'badge' },
                { key: 'stock', label: this.$t('food.columns.stock'), sortable: true, type: 'number' },
                { key: 'actions', label: this.$t('food.columns.actions'), sortable: false, type: 'actions' },
            ]
        },
        paginatedItems() {
            return this.items
        },
        addModalTitle() {
            const cat = this.optionCategories.find((c) => c.id === this.optionForm.categoryId)
            return cat
                ? `${this.$t('food.options.add_modal_title')} ${cat.name.toLowerCase()}`
                : this.$t('food.options.add_option')
        },
        vatRates() {
            // Get VAT rates from establishment data
            const tvas = this.establishment?.pays?.tvas || []
            if (tvas.length > 0) {
                return tvas.map((tva) => ({
                    value: tva.pourcentage,
                    label: `${tva.pourcentage}%`,
                }))
            }
            return []
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,

        // ----------- Option Group Drag & Drop -----------
        onOptDragStart(evt, index) {
            evt.dataTransfer.setData('text/plain', index)
            evt.dataTransfer.effectAllowed = 'move'
            this._optDraggedIndex = index
        },
        onOptDragOver(evt) {
            evt.preventDefault()
            evt.dataTransfer.dropEffect = 'move'
        },
        onOptDrop(evt, targetIndex) {
            evt.preventDefault()
            const from = this._optDraggedIndex
            if (from !== null && from !== targetIndex) {
                const items = [...this.optionGroupForm.items]
                const moved = items.splice(from, 1)[0]
                items.splice(targetIndex, 0, moved)
                this.optionGroupForm.items = items
            }
            this._optDraggedIndex = null
            evt.currentTarget.classList.remove('drag-over')
        },
        onOptDragEnter(evt) {
            evt.currentTarget.classList.add('drag-over')
        },
        onOptDragLeave(evt) {
            if (!evt.currentTarget.contains(evt.relatedTarget)) {
                evt.currentTarget.classList.remove('drag-over')
            }
        },
        onOptDragEnd() {
            this._optDraggedIndex = null
            document.querySelectorAll('.opt-drag-item').forEach((el) => el.classList.remove('drag-over'))
        },

        // ----------- Supplement Group Drag & Drop -----------
        onSuppDragStart(evt, index) {
            evt.dataTransfer.setData('text/plain', index)
            evt.dataTransfer.effectAllowed = 'move'
            this._suppDraggedIndex = index
        },
        onSuppDragOver(evt) {
            evt.preventDefault()
            evt.dataTransfer.dropEffect = 'move'
        },
        onSuppDrop(evt, targetIndex) {
            evt.preventDefault()
            const from = this._suppDraggedIndex
            if (from !== null && from !== targetIndex) {
                const items = [...this.supplementGroupForm.items]
                const moved = items.splice(from, 1)[0]
                items.splice(targetIndex, 0, moved)
                this.supplementGroupForm.items = items
            }
            this._suppDraggedIndex = null
            evt.currentTarget.classList.remove('drag-over')
        },
        onSuppDragEnter(evt) {
            evt.currentTarget.classList.add('drag-over')
        },
        onSuppDragLeave(evt) {
            if (!evt.currentTarget.contains(evt.relatedTarget)) {
                evt.currentTarget.classList.remove('drag-over')
            }
        },
        onSuppDragEnd() {
            this._suppDraggedIndex = null
            document.querySelectorAll('.supp-drag-item').forEach((el) => el.classList.remove('drag-over'))
        },

        // Close dropdown when clicking outside
        handleClickOutside(event) {
            if (this.$refs.addDropdown && !this.$refs.addDropdown.contains(event.target)) {
                this.showAddDropdown = false
            }
        },
        getState(field) {
            if (this.errors[field]) return 'error'
            return 'default'
        },

        getItemState(field, index) {
            if (this.errors.items?.[index]?.[field]) return 'error'
            return 'default'
        },

        clearError(field) {
            if (this.errors[field]) {
                this.$delete(this.errors, field)
            }
        },

        clearItemError(field, index) {
            if (this.errors.items?.[index]?.[field]) {
                this.$delete(this.errors.items[index], field)
            }
        },
        // Open Modals
        openAddOptionGroupModal() {
            this.showAddDropdown = false
            this.isEditMode = false
            this.editingId = null
            this.errors = { items: [] }
            this.optionGroupForm = { name: '', items: [{ name: '', price: 0, vat_rate: null }] }
            this.showOptionGroupModal = true
        },
        openAddSupplementGroupModal() {
            this.showAddDropdown = false
            this.isEditMode = false
            this.editingId = null
            this.errors = { items: [] }
            this.supplementGroupForm = {
                name: '',
                min_quantity: 0,
                max_quantity: 0,
                optional: true,
                items: [
                    {
                        name: '',
                        price: 0,
                        original_price: 0,
                        vat_rate: null,
                        is_free: false,
                        is_required: false,
                        force_one_qty: false,
                        active: true,
                    },
                ],
            }
            this.showSupplementGroupModal = true
        },

        async openEditModal(item) {
            try {
                this.isEditMode = true
                this.editingId = item.id
                this.editingSource = item.source || 'food_attributes'
                this.errors = { items: [] }

                // Determine if it's an "Option" (FoodAttribute) or "Supplement" (FoodCategoryAddon)
                const isOptionType = item.type === 'attribute' || item.source === 'food_attributes'

                if (isOptionType) {
                    // Fetch from FoodAttributes API
                    const res = await axios.get(`/back-office/v3/food/attributes/show/${item.id}`)
                    const attr = res.data.data

                    // Populate Option Group Form
                    this.optionGroupForm = {
                        name: attr.name,
                        items: (attr.values || []).map((v) => ({
                            id: v.id,
                            name: v.key || v.name || '',
                            price: parseFloat(v.price) || parseFloat(v.value) || 0,
                            vat_rate: v.vat_rate ? parseFloat(v.vat_rate) : null,
                        })),
                    }
                    if (this.optionGroupForm.items.length === 0) {
                        this.optionGroupForm.items.push({ name: '', price: 0, vat_rate: null })
                    }
                    this.showOptionGroupModal = true
                } else {
                    // Fetch from FoodCategoryAddons API
                    const res = await axios.get(`/back-office/v3/food/addons/show/${item.id}`)
                    const addon = res.data.data

                    // Populate Supplement Group Form
                    this.supplementGroupForm = {
                        name: addon.name,
                        min_quantity: addon.min_quantity || 0,
                        max_quantity: addon.max_quantity || 0,
                        optional: !addon.is_qty_required,
                        items: (addon.items || []).map((v) => ({
                            id: v.id,
                            name: v.name || '',
                            // If free, visual price is 0, but original_price holds the DB value
                            price: v.is_free ? 0 : parseFloat(v.price) || 0,
                            original_price: parseFloat(v.price) || 0,
                            vat_rate: v.vat_rate ? parseFloat(v.vat_rate) : null,
                            is_free: Boolean(v.is_free),
                            is_required: Boolean(v.is_required),
                            force_one_qty: Boolean(v.force_one_qty),
                            active: v.active === true || v.active === 1 || v.active === '1',
                        })),
                    }
                    if (this.supplementGroupForm.items.length === 0) {
                        this.supplementGroupForm.items.push({
                            name: '',
                            price: 0,
                            original_price: 0,
                            vat_rate: null,
                            is_free: false,
                            active: true,
                        })
                    }
                    this.showSupplementGroupModal = true
                }
            } catch (e) {
                console.error(e)
                alert(this.$t('food.messages.load_error'))
            }
        },

        // Group Item Helpers
        addOptionGroupItem() {
            this.optionGroupForm.items.push({ name: '', price: 0, vat_rate: null })
        },
        removeOptionGroupItem(index) {
            if (this.optionGroupForm.items.length > 1) {
                this.optionGroupForm.items.splice(index, 1)
            }
        },
        addSupplementGroupItem() {
            this.supplementGroupForm.items.push({
                name: '',
                price: 0,
                original_price: 0,
                vat_rate: null,
                is_free: false,
                is_required: false,
                force_one_qty: false,
                active: true,
            })
        },
        removeSupplementGroupItem(index) {
            if (this.supplementGroupForm.items.length > 1) {
                this.supplementGroupForm.items.splice(index, 1)
            }
        },

        // Save Logic
        async saveOptionGroup() {
            // Clear previous errors
            this.errors = { items: [] }
            let hasErrors = false

            // Validate name
            if (!this.optionGroupForm.name) {
                this.$set(this.errors, 'name', this.$t('food.messages.option_name_required'))
                hasErrors = true
            }

            // Validate items - must have name and price > 0
            for (let i = 0; i < this.optionGroupForm.items.length; i++) {
                const item = this.optionGroupForm.items[i]
                const itemErrors = {}

                if (!item.name || item.name.trim() === '') {
                    itemErrors.name = this.$t('food.messages.option_item_name_required', { index: i + 1 })
                    hasErrors = true
                }
                if (item.price === null || item.price === '' || parseFloat(item.price) <= 0) {
                    itemErrors.price = this.$t('food.messages.option_item_price_required', { index: i + 1 })
                    hasErrors = true
                }

                if (Object.keys(itemErrors).length > 0) {
                    this.$set(this.errors.items, i, itemErrors)
                }
            }

            if (hasErrors) return

            try {
                const payload = {
                    name: this.optionGroupForm.name,
                    min_quantity: 1,
                    max_quantity: 1,
                    is_required: true,
                    items: this.optionGroupForm.items,
                }

                if (this.isEditMode) {
                    await axios.post(`/back-office/v3/food/attributes/update/${this.editingId}`, payload)
                } else {
                    await axios.post('/back-office/v3/food/attributes/store', payload)
                }

                this.errors = { items: [] }
                this.showOptionGroupModal = false
                this.fetchData()
                this.$emit('refresh-stats')
            } catch (e) {
                console.error(e)
                this.$set(this.errors, 'name', this.$t('food.messages.save_error'))
            }
        },

        async saveSupplementGroup() {
            // Clear previous errors
            this.errors = { items: [] }
            let hasErrors = false

            // Validate name
            if (!this.supplementGroupForm.name) {
                this.$set(this.errors, 'name', this.$t('food.messages.supplement_name_required'))
                hasErrors = true
            }

            // Validate items - must have name and price > 0
            for (let i = 0; i < this.supplementGroupForm.items.length; i++) {
                const item = this.supplementGroupForm.items[i]
                const itemErrors = {}

                if (!item.name || item.name.trim() === '') {
                    itemErrors.name = this.$t('food.messages.option_item_name_required', { index: i + 1 })
                    hasErrors = true
                }
                if (!item.is_free && (item.price === null || item.price === '' || parseFloat(item.price) <= 0)) {
                    itemErrors.price = this.$t('food.messages.option_item_price_required', { index: i + 1 })
                    hasErrors = true
                }

                if (Object.keys(itemErrors).length > 0) {
                    this.$set(this.errors.items, i, itemErrors)
                }
            }

            if (hasErrors) return

            try {
                const payload = {
                    name: this.supplementGroupForm.name,
                    min_quantity: parseInt(this.supplementGroupForm.min_quantity) || 0,
                    max_quantity: parseInt(this.supplementGroupForm.max_quantity) || 0,
                    is_qty_required: Boolean(!this.supplementGroupForm.optional),
                    items: this.supplementGroupForm.items.map((item) => ({
                        id: item.id || null,
                        name: item.name,
                        // If free, save original_price to DB to preserve it. If not free, save current price.
                        price: item.is_free ? parseFloat(item.original_price) || 0 : parseFloat(item.price) || 0,
                        vat_rate: parseFloat(item.vat_rate) || 10,
                        is_free: Boolean(item.is_free),
                        is_required: Boolean(item.is_required),
                        force_one_qty: Boolean(item.force_one_qty),
                        active: item.active !== false,
                    })),
                }

                if (this.isEditMode) {
                    await axios.post(`/back-office/v3/food/addons/update/${this.editingId}`, payload)
                } else {
                    await axios.post('/back-office/v3/food/addons/store', payload)
                }

                this.errors = { items: [] }
                this.showSupplementGroupModal = false
                this.fetchData()
                this.$emit('refresh-stats')
            } catch (e) {
                console.error(e)
                this.$set(this.errors, 'name', this.$t('food.messages.save_error'))
            }
        },

        toSlug(text) {
            return text
                .toLowerCase()
                .replace(/ /g, '-')
                .replace(/[^\w-]+/g, '')
        },

        formatPrice(price) {
            return Number(price || 0)
                .toFixed(2)
                .replace('.', ',')
        },
        handlePageChange(page) {
            this.currentPage = page
            this.fetchData()
        },
        handlePerPageChange(val) {
            this.itemsPerPage = Number(val)
            setPerPage('food_options_items_per_page', this.itemsPerPage)
            this.currentPage = 1
            this.fetchData()
        },
        handleSearchUpdate(val) {
            this.searchQuery = val
            this.currentPage = 1
            this.fetchData()
        },
        toggleItemSelection(itemId) {
            const index = this.selectedItems.indexOf(itemId)
            if (index > -1) {
                this.selectedItems.splice(index, 1)
            } else {
                this.selectedItems.push(itemId)
            }
        },
        toggleSelectAll() {
            if (this.selectedItems.length === this.paginatedItems.length) {
                this.selectedItems = []
            } else {
                this.selectedItems = this.paginatedItems.map((item) => item.id)
            }
        },
        toggleFree(item) {
            if (!item.is_free) {
                // Switching to FREE
                item.original_price = item.price
                item.price = 0
                item.is_free = true
            } else {
                // Switching to PAID
                item.is_free = false
                if (item.original_price !== undefined && item.original_price !== null && item.original_price !== '') {
                    item.price = item.original_price
                }
            }
        },

        async toggleStatus(item) {
            const previousStatus = item.status
            const newStatus = previousStatus === 'actif' ? 'inactif' : 'actif'
            item.status = newStatus

            try {
                const response = await axios.patch('/back-office/v3/food/options-and-addons/update-status', {
                    id: item.id,
                    active: newStatus === 'actif',
                    type: item.type, // 'attribute' or 'addon'
                })

                if (!response.data.success) {
                    item.status = previousStatus
                    alert(this.$t('food.messages.status_update_error'))
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                item.status = previousStatus
            }
        },
        deleteSingleItem(item) {
            this.deleteAction = 'single'
            this.itemToDelete = item
            this.deletePopupDescription = this.$t('food.messages.confirm_delete_option')
            this.showDeletePopup = true
        },
        deleteSelectedItems() {
            if (this.selectedItems.length === 0) return
            this.deleteAction = 'multiple'
            this.deletePopupDescription = this.$t('food.messages.confirm_delete_options', { count: this.selectedItems.length })
            this.showDeletePopup = true
        },
        async confirmDelete() {
            try {
                if (this.deleteAction === 'single') {
                    const item = this.itemToDelete
                    if (item.source === 'food_category_addons' || item.type === 'addon') {
                        await axios.delete('/back-office/v3/food/addons/delete', { data: { ids: [item.id] } })
                    } else {
                        // For FoodAttributes, we'll keep using the existing delete (or create a new one)
                        await axios.delete('/back-office/v3/food/attributes/delete', { data: { ids: [item.id] } })
                    }
                } else {
                    // For bulk delete - need to separate by type
                    const attributeIds = []
                    const addonIds = []
                    this.items
                        .filter((i) => this.selectedItems.includes(i.id))
                        .forEach((item) => {
                            if (item.source === 'food_category_addons' || item.type === 'addon') {
                                addonIds.push(item.id)
                            } else {
                                attributeIds.push(item.id)
                            }
                        })

                    if (attributeIds.length > 0) {
                        await axios.delete('/back-office/v3/food/attributes/delete', { data: { ids: attributeIds } })
                    }
                    if (addonIds.length > 0) {
                        await axios.delete('/back-office/v3/food/addons/delete', { data: { ids: addonIds } })
                    }
                }

                this.fetchData()
                this.selectedItems = []
                this.showDeletePopup = false
                this.$emit('refresh-stats')
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Delete error:', error)
                alert(this.$t('food.messages.delete_error'))
            }
        },
        openEditTab(item) {
            if (item.type === 'attribute' || !item.category_id) {
                this.openEditModal(item)
            } else {
                window.location.href = `/back-office/v3/food/edit/${item.id}/food`
            }
        },
        openCreateTab() {
            window.location.href = '/back-office/v3/food/create/food'
        },
        async openAddModal() {
            this.resetOptionForm()
            if (this.optionCategories.length === 0) {
                await this.fetchOptionCategories()
            }
            this.showAddModal = true
        },
        resetOptionForm() {
            this.optionForm = {
                categoryId: null,
                name: '',
                price: 0,
                vatRate: null,
                description: '',
                active: true,
            }
        },
        async fetchOptionCategories() {
            try {
                // Fetch categories matching our category slugs
                const res = await axios.get('/back-office/v3/food/categories/fast-list')
                const allCats = res.data.data || []
                // Filter to only show option-type categories
                this.optionCategories = allCats.filter((cat) =>
                    ['complements', 'supplements', 'boissons', 'sauces'].includes(cat.slug)
                )
            } catch (e) {
                console.error(e)
            }
        },
        async saveOption() {
            try {
                await axios.post('/back-office/v3/food/store', {
                    name: this.optionForm.name,
                    category_id: this.optionForm.categoryId,
                    price: this.optionForm.price,
                    vat_rate: this.optionForm.vatRate,
                    description: this.optionForm.description,
                    active: this.optionForm.active,
                })
                this.showAddModal = false
                this.fetchData()
                this.$emit('refresh-stats')
            } catch (e) {
                console.error(e)
                alert(this.$t('food.messages.create_error'))
            }
        },

        duplicateItem(item) {
            if (!item) return

            this.itemToDuplicate = item
            // The user requested: "Êtes-vous sûr de vouloir dupliquer ce supplément ?"
            // We can adjust based on type if needed, but the request was specific.
            // Let's keep it dynamic for correctness:
            const isAttribute = item.type === 'attribute'
            this.duplicatePopupDescription = isAttribute
                ? this.$t('food.messages.confirm_duplicate_option')
                : this.$t('food.messages.confirm_duplicate_supplement')
            this.showDuplicatePopup = true
        },
        async confirmDuplicate() {
            if (!this.itemToDuplicate) return

            const item = this.itemToDuplicate

            // Use item.type directly as it comes from API as 'attribute' or 'addon'
            const type = item.type === 'attribute' ? 'attribute' : 'addon'

            // As requested, always call addons/duplicate, but passing type
            const url = '/back-office/v3/food/addons/duplicate'

            try {
                const response = await axios.post(url, {
                    id: item.id,
                    type: type,
                })
                if (response.data.success) {
                    this.$toast?.success(
                        type === 'attribute'
                            ? this.$t('food.messages.option_duplicated_success')
                            : this.$t('food.messages.supplement_duplicated_success')
                    )
                    this.fetchData()
                    this.$emit('refresh-stats')
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error duplicating item:', error)
                alert(this.$t('food.messages.duplicate_error'))
            } finally {
                this.showDuplicatePopup = false
                this.itemToDuplicate = null
            }
        },
        async fetchData() {
            try {
                const params = {
                    search: this.searchQuery,
                    currentPage: this.currentPage,
                    itemsPerPage: this.itemsPerPage,
                    sortBy: this.sortBy,
                    sortOrder: this.sortOrder,
                }
                const response = await axios.get('/back-office/v3/food/options-and-addons/list', { params })
                this.items = (response.data.data || []).map((item) => ({
                    ...item,
                    _uid: `${item.type || ''}-${item.id}`,
                }))
                this.totalPages = response.data.last_page || 1
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error fetching data:', error)
            }
        },
        async handleDragEnd(event) {
            const { oldIndex, newIndex } = event

            if (oldIndex === newIndex) return

            // Move the item locally
            const movedItem = this.items.splice(oldIndex, 1)[0]
            this.items.splice(newIndex, 0, movedItem)

            // Calculate start order index based on pagination
            const startOrder = (this.currentPage - 1) * this.itemsPerPage
            const changedPositions = []

            // Re-assign order based on new index
            this.items.forEach((item, index) => {
                const newOrder = startOrder + index

                changedPositions.push({
                    id: item.id,
                    order: newOrder,
                    type: item.type, // Required for OptionController
                })
                item.order = newOrder
            })

            // Save if there are changes
            if (changedPositions.length > 0) {
                await this.savePositions(changedPositions)
            }
        },
        async savePositions(positions) {
            if (this.isSavingPositions) return
            this.isSavingPositions = true

            try {
                await axios.put('/back-office/v3/food/options-and-addons/update-positions', {
                    positions,
                })
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error saving positions:', error)
                this.fetchData()
            } finally {
                this.isSavingPositions = false
            }
        },
        handleSort(key) {
            if (this.sortBy === key) {
                this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
            } else {
                this.sortBy = key
                this.sortOrder = 'asc'
            }
            this.fetchData()
        },
        async exportData(format = 'csv') {
            if (this.isExporting) return

            this.isExporting = true
            try {
                const params = new URLSearchParams({ format })
                this.categorySlugs.forEach((slug) => {
                    params.append('category_slugs[]', slug)
                })

                const response = await axios.get(`/back-office/v3/food/options/export?${params.toString()}`, {
                    responseType: 'blob',
                })

                const url = window.URL.createObjectURL(new Blob([response.data]))
                const link = document.createElement('a')
                link.href = url

                const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition']
                let fileName = `options_export.${format === 'pdf' ? 'pdf' : format === 'excel' ? 'xlsx' : 'csv'}`
                if (contentDisposition) {
                    const fileNameMatch = contentDisposition.match(/filename=(?:"([^"]+)"|([^;]+))/)
                    if (fileNameMatch) {
                        fileName = fileNameMatch[1] || fileNameMatch[2]
                    }
                }

                link.setAttribute('download', fileName)
                document.body.appendChild(link)
                link.click()

                document.body.removeChild(link)
                window.URL.revokeObjectURL(url)
            } catch (e) {
                console.error(e)
                alert(this.$t('food.messages.export_error'))
            } finally {
                this.isExporting = false
            }
        },
    },
    mounted() {
        this.fetchData()
        document.addEventListener('click', this.handleClickOutside)
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside)
    },
}
</script>

<style scoped lang="scss">
.border_btns {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    border-radius: 7px;
    border: 1px solid #545595;
    min-height: 40px;
    width: 40px;
    overflow: hidden;
}
.transparent-btn {
    width: 100% !important;
    height: 100% !important;
    box-shadow: none !important;
    position: absolute;
    left: 0;
    top: 0;
    border: none;
    min-width: auto !important;
}
.transparent-btn:hover {
    background: #d9d9d91a !important;
}
.transparent-btn-icon {
    box-shadow: none !important;
    min-width: auto !important;
}
.ppv2-cursor-grab {
    cursor: grab;
}
.ppv2-cursor-grab:active {
    cursor: grabbing;
}
.opt-drag-item.drag-over,
.supp-drag-item.drag-over {
    outline: 2px dashed #7d2dbd;
    outline-offset: 2px;
    background-color: rgba(125, 45, 189, 0.06) !important;
}
</style>
